import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  TableContainer,
  Spinner,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Flex,
  useToast,
  useDisclosure,
  Image,
  Select,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import Navbar from "../../Navbar/Navbar";
import Sidebar from "../../Sidebar";
import axios from "axios";
import { FiMoreVertical, FiEdit, FiPlus } from "react-icons/fi";
import { api_url } from "../../../url";
import AddGameModal from "./AddGameModal";
import EditGameModal from "./EditGameModal";

const SIDEBAR_WIDTH = "240px";
const ITEMS_PER_PAGE = 10;

export default function AllGames() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [games, setGames] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);

  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const toast = useToast();

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchGames();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, page, selectedProvider]);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${api_url}/admin/provider?limit=100`, {
        // Fetching more to populate dropdown
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.success) {
        setProviders(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch providers", error);
    }
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = {
        page: page,
        limit: ITEMS_PER_PAGE,
      };

      if (search) {
        params.name = search;
      }

      if (selectedProvider) {
        params.providerName = selectedProvider;
      }

      const { data } = await axios.get(`${api_url}/admin/games`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data.success) {
        setGames(data.data || []);
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        setGames([]);
      }
    } catch (error) {
      console.error("Failed to fetch games", error);
      toast({
        title: "Error fetching games",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setGames([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (game) => {
    setSelectedGame(game);
    onEditOpen();
  };

  const handleSuccess = () => {
    fetchGames();
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        {/* HEADER */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
          mb={6}
        >
          <Box>
            <Text fontSize="2xl" fontWeight="700" color="#344767">
              All Casino Games
            </Text>
          </Box>

          <Flex gap={4} direction={{ base: "column", md: "row" }}>
            <Select
              placeholder="Filter by Provider"
              maxW={{ base: "100%", md: "200px" }}
              value={selectedProvider}
              onChange={(e) => {
                setSelectedProvider(e.target.value);
                setPage(1);
              }}
              bg="white"
            >
              {providers.map((p) => (
                <option key={p._id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </Select>

            <InputGroup maxW={{ base: "100%", md: "300px" }}>
              <Input
                placeholder="Search Games..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                bg="white"
                borderRadius="8px"
              />
              <InputRightElement color="gray.400">
                <SearchIcon />
              </InputRightElement>
            </InputGroup>

            <Button
              paddingX={10}
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onAddOpen}
            >
              Add Game
            </Button>
          </Flex>
        </Flex>

        {/* TABLE CONTAINER */}
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="sm"
          overflow="hidden"
          border="1px solid #f0f2f5"
        >
          <TableContainer>
            <Table variant="simple">
              <Thead bg="#1f15da">
                <Tr>
                  <Th color="white" py={4}>
                    Game
                  </Th>
                  <Th color="white" py={4}>
                    Provider
                  </Th>
                  <Th color="white" py={4}>
                    Category
                  </Th>
                  <Th color="white" py={4}>
                    Type
                  </Th>
                  <Th color="white" py={4}>
                    Status
                  </Th>
                  <Th color="white" py={4}>
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                ) : games.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      No games found.
                    </Td>
                  </Tr>
                ) : (
                  games.map((g) => (
                    <Tr key={g._id} _hover={{ bg: "#f8f9fa" }}>
                      <Td>
                        <Flex align="center" gap={3}>
                          {g.thumbnail && (
                            <Image
                              src={g.thumbnail}
                              boxSize="40px"
                              objectFit="cover"
                              borderRadius="md"
                              fallbackSrc="https://via.placeholder.com/40"
                            />
                          )}
                          <Box>
                            <Text fontWeight="600">{g.name}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {g.gameCode || g.providerGameId}
                            </Text>
                          </Box>
                        </Flex>
                      </Td>
                      <Td fontSize="sm">
                        {g.providerName ||
                          (g.provider && g.provider.name) ||
                          "-"}
                      </Td>
                      <Td fontSize="sm">{g.category || "-"}</Td>
                      <Td fontSize="sm">{g.gameType || "-"}</Td>
                      <Td>
                        <Badge colorScheme={g.status ? "green" : "red"}>
                          {g.status ? "Active" : "Inactive"}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEdit />}
                              onClick={() => handleEdit(g)}
                            >
                              Edit
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* PAGINATION */}
        {!loading && totalPages > 1 && (
          <Flex justify="center" mt={8} gap={3}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(page - 1)}
              isDisabled={page === 1}
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ bg: "gray.50" }}
            >
              Prev
            </Button>

            {[...Array(totalPages)].map((_, i) => (
              <Button
                key={i}
                size="sm"
                onClick={() => setPage(i + 1)}
                bg={page === i + 1 ? "#5e72e4" : "white"}
                color={page === i + 1 ? "white" : "gray.600"}
                border="1px solid"
                borderColor={page === i + 1 ? "#5e72e4" : "gray.200"}
                _hover={{
                  bg: page === i + 1 ? "#4e62d4" : "gray.50",
                }}
                minW="40px"
                borderRadius="md"
              >
                {i + 1}
              </Button>
            ))}

            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(page + 1)}
              isDisabled={page === totalPages}
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ bg: "gray.50" }}
            >
              Next
            </Button>
          </Flex>
        )}
      </Box>

      <AddGameModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSuccess={handleSuccess}
        providers={providers}
      />
      {selectedGame && (
        <EditGameModal
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setSelectedGame(null);
          }}
          game={selectedGame}
          onSuccess={handleSuccess}
          providers={providers}
        />
      )}
    </>
  );
}
